import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Home } from '@/pages/Home';
import { Dashboard } from '@/pages/Dashboard';
import { Login, Register } from '@/pages/Auth';
import { Study } from '@/pages/Study';
import { Games } from '@/pages/Games';
import { Flashcards } from '@/pages/Flashcards';
import { MatchGame } from '@/pages/MatchGame';
import { Quiz } from '@/pages/Quiz';
import { MorphemeGame } from '@/pages/MorphemeGame';
import { MorphemeExplorer } from '@/pages/MorphemeExplorer';
import { MorphemeDetail } from '@/pages/MorphemeDetail';
import { TermDetail } from '@/pages/TermDetail';
import { ProgressPage } from '@/pages/Progress';
import { Guide } from '@/pages/Guide';
import { Profile } from '@/pages/Profile';
import { Contact } from '@/pages/Contact';
import { FAQ } from '@/pages/FAQ';
import { Legal } from '@/pages/Legal';
import { Pricing } from '@/pages/Pricing';
import { Welcome } from '@/pages/Welcome';
import { syncProgressFromFirestore } from '@/utils/storage';
import { auth } from '@/firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { getPaddle } from '@/services/paddle';
import { LanguageProvider } from '@/context/LanguageContext';
import { CookieBanner } from '@/components/CookieBanner';
import { initAnalyticsOnLoad } from '@/services/analytics';
import { initMetaPixelOnLoad } from '@/services/metaPixel';
import { MetaPixelRouteTracker } from '@/components/MetaPixelRouteTracker';
import { getPreviewRole } from '@/utils/planAccess';
import './App.css';

/**
 * Robust, asynchronous Auth Guard:
 * Eliminates race condition flickers and prevents forged localStorage bypasses.
 */
const ProtectedRoute = ({ children }) => {
  const previewRole = getPreviewRole();

  const [authState, setAuthState] = useState({
    user: previewRole ? { uid: 'preview-uid', email: 'dr.kaya@healthlexmed.com', displayName: 'Dr. Ahmet Kaya' } : null,
    loading: !previewRole
  });

  useEffect(() => {
    if (previewRole) return;
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setAuthState({
        user: currentUser,
        loading: false
      });
    });

    return () => unsubscribe();
  }, [previewRole]);

  if (authState.loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background">
        <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin mb-3" />
        <span className="text-xs text-muted-foreground animate-pulse font-medium">Doğrulanıyor...</span>
      </div>
    );
  }

  if (!authState.user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

/**
 * Global AppFooter:
 * Bilgi, çalışma, sözlük, profil ve tarife sayfalarında Footer'ı otomatik render eder.
 * Tüm oyun sayfalarında (/games, /flashcards, /match, /quiz, /morpheme) ise footer'ı kesinlikle gizler.
 */
const AppFooter = () => {
  const location = useLocation();
  const p = (location.pathname || '').toLowerCase().replace(/\/+$/, '') || '/';

  // Oyun sayfalarında (menü veya oynanış modu fark etmeksizin) footer kesinlikle gösterilmez
  const isGameRoute =
    p === '/games' ||
    p.startsWith('/games/') ||
    p === '/flashcards' ||
    p.startsWith('/flashcards/') ||
    p === '/match' ||
    p.startsWith('/match/') ||
    p === '/quiz' ||
    p.startsWith('/quiz/') ||
    (p.startsWith('/morpheme') && !p.startsWith('/morphemes'));

  if (isGameRoute) {
    return null;
  }
  return <Footer />;
};

/**
 * Automatically scrolls to top on route navigation, or scrolls to element if hash is present.
 */
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace('#', ''));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        return;
      }
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname, hash]);

  return null;
};

function App() {
  useEffect(() => {
    getPaddle(); // Pre-warm & initialize Paddle.js with live client-side token
    initAnalyticsOnLoad(); // Check cookie consent and boot Google Analytics if accepted
    initMetaPixelOnLoad(); // Boot Meta Pixel only with marketing consent (and listen for later changes)

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        syncProgressFromFirestore();
      }
    });

    return () => unsubscribe();
  }, []);


  return (
    <LanguageProvider>
      <BrowserRouter>
        <ScrollToTop />
        <MetaPixelRouteTracker />
        <div className="App min-h-screen bg-background flex flex-col justify-between">
          <Navbar />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/panel" element={<Navigate to="/dashboard" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/study" element={<Study />} />
            <Route path="/study/:slug" element={<TermDetail />} />
            <Route path="/terms/:slug" element={<TermDetail />} />
            <Route path="/games" element={<Games />} />
            <Route path="/flashcards" element={<Flashcards />} />
            <Route path="/match" element={<MatchGame />} />
            <Route path="/quiz" element={<Quiz />} />
            <Route path="/morpheme" element={<MorphemeGame />} />
            <Route path="/morphemes" element={<MorphemeExplorer />} />
            <Route path="/morfemler" element={<Navigate to="/morphemes" replace />} />
            <Route path="/morphemes/:slug" element={<MorphemeDetail />} />
            <Route path="/morpheme-explorer" element={<MorphemeExplorer />} />
            <Route path="/rehber" element={<Guide />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/iletisim" element={<Navigate to="/contact" replace />} />
            <Route path="/hakkinda" element={<Navigate to="/contact" replace />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/sss" element={<Navigate to="/faq" replace />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/terms" element={<Legal activeDoc="terms" />} />
            <Route path="/kullanim-kosullari" element={<Navigate to="/terms" replace />} />
            <Route path="/privacy" element={<Legal activeDoc="privacy" />} />
            <Route path="/gizlilik" element={<Navigate to="/privacy" replace />} />
            <Route path="/refund" element={<Legal activeDoc="refund" />} />
            <Route path="/iptal-iade" element={<Navigate to="/refund" replace />} />
            <Route path="/cerez" element={<Navigate to="/privacy" replace />} />
            <Route path="/legal" element={<Navigate to="/terms" replace />} />
            <Route path="/progress" element={<ProgressPage />} />
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/success" element={<Navigate to="/welcome" replace />} />
            <Route path="/profile" element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <AppFooter />
          <Toaster position="top-right" richColors />
          <CookieBanner />
        </div>
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;